/**
 * AgencySelect — the GAS-style account filter for Vendor Competitive Analysis.
 *
 * A compact on-page dropdown that scopes every tab to one agency (or back to
 * statewide), independent of account lists. GAS has had this from day one via
 * its global department filter; TekStak only offered list-based scoping, so
 * "just show me this vendor at CDCR" required building a list first. This
 * removes that detour.
 *
 * Same interaction pattern as VendorPicker: trigger button, search field
 * inside the open panel, click-outside closes. Shares the ['departments',
 * 'all-for-matching'] query key with AccountLists so the directory is fetched
 * once per session.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronDown } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';

function AgencySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['departments', 'all-for-matching'],
    queryFn: () => apiClient.get('/api/departments'),
    staleTime: 5 * 60 * 1000,
  });

  const departments = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return [...list].sort((a, b) =>
      (a.acronym || a.official_name || '').localeCompare(b.acronym || b.official_name || '')
    );
  }, [data]);

  const selected = useMemo(
    () => departments.find(d => d.department_id === value) || null,
    [departments, value]
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter(d =>
      [d.acronym, d.official_name, d.common_name]
        .filter(Boolean)
        .some(s => s.toLowerCase().includes(q))
    );
  }, [departments, query]);

  useEffect(() => {
    function onClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pick = (dept) => {
    logger.vendorAnalysis.info('AgencySelect:selected', {
      department_id: dept?.department_id ?? null,
      acronym: dept?.acronym ?? 'statewide',
    });
    onChange(dept ? dept.department_id : null, dept);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="vca-picker vca-picker--agency" ref={containerRef}>
      <button
        type="button"
        className={`vca-picker__trigger ${value ? 'is-scoped' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        title="Scope every tab to one agency"
      >
        <Building2 size={15} />
        <span className="vca-picker__trigger-label">
          {selected
            ? `${selected.acronym || selected.official_name}`
            : isLoading ? 'Loading…' : 'All agencies'}
        </span>
        <ChevronDown size={15} className="vca-picker__chevron" />
      </button>
      {open && (
        <div className="vca-picker__results" role="listbox">
          <input
            type="text"
            className="vca-picker__search"
            placeholder="Search agencies…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div
            className={`vca-picker__row ${!value ? 'active' : ''}`}
            role="option"
            aria-selected={!value}
            onClick={() => pick(null)}
          >
            <div className="vca-picker__row-name">All agencies</div>
            <div className="vca-picker__row-meta"><span>statewide</span></div>
          </div>
          {results.map(d => (
            <div
              key={d.department_id}
              className={`vca-picker__row ${d.department_id === value ? 'active' : ''}`}
              role="option"
              aria-selected={d.department_id === value}
              onClick={() => pick(d)}
            >
              <div className="vca-picker__row-name">
                {d.acronym || d.official_name}
              </div>
              <div className="vca-picker__row-meta">
                <span>{d.official_name}</span>
              </div>
            </div>
          ))}
          {!results.length && (
            <div className="vca-picker__row vca-picker__row--empty">
              <div className="vca-picker__row-meta"><span>No agency matches “{query}”</span></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AgencySelect;
